/**
 * Where to send the user when they switch tenant while sitting on a URL that
 * names a specific record.
 *
 * A detail route carries an id (an MRN, a procedure id, a submission id) that
 * belongs to exactly one tenant, so after a switch that record does not exist:
 * the page would remount, 404, and leave the user staring at an error with no
 * way back. Sending them to the equivalent list for the tenant they just moved
 * into is the only useful destination.
 *
 * Only routes naming a record belong here. Static siblings like /patients/new
 * are excluded — they reference nothing, so remounting them is enough. Wiki
 * pages are deliberately absent: /wiki/:section/:slug is documentation, not
 * tenant data, and bouncing a reader to a list would be a bug.
 */

// Static paths that look like detail routes but name no record.
const NON_RECORD_PATHS = new Set(['/patients/new', '/integrations/new', '/form-builder/new'])

const FALLBACKS: ReadonlyArray<readonly [RegExp, string]> = [
  // Covers both /patients/:mrn and /patients/:mrn/procedures/:procedureId.
  [/^\/patients\/[^/]+/, '/patients'],
  [/^\/integrations\/[^/]+/, '/integrations'],
  // The trailing slash is required: only /procedure-templates/:id/sequence exists.
  [/^\/procedure-templates\/[^/]+\//, '/procedure-templates'],
  [/^\/forms\/[^/]+/, '/forms'],
  [/^\/inventory\/pick-lists\/[^/]+/, '/inventory/pick-lists'],
  // Covers /products/:id. The Products TAB, not the page's default Storage
  // tab: somebody sitting on a product belongs in the product list.
  [/^\/products\/[^/]+/, '/products?tab=products'],
  // A storage unit's node id belongs to one tenant; after a switch it names
  // nothing, so fall back to the storage list rather than an empty freezer.
  // The pattern also covers /inventory/storage/node/:nodeId.
  [/^\/inventory\/storage\/[^/]+/, '/inventory?tab=storage'],
  // A transfer plan belongs to one tenant's pair of freezers. Listed
  // separately because the storage pattern above will not match it — the
  // segment is `storage-transfers`, not `storage` — and it falls back to the
  // plans LIST now that one exists, which is the equivalent list this module
  // asks for rather than the nearest screen.
  [/^\/inventory\/storage-transfers\/[^/]+/, '/inventory/storage-transfers'],
  [/^\/inventory\/kits\/builds\/[^/]+/, '/inventory?tab=kits'],
  // Same story on the configuration side: a layout belongs to one tenant's unit.
  [/^\/equipment\/storage\/[^/]+/, '/equipment?tab=storage'],
  // A kit run is a procedure owned by one tenant, same as a patient procedure.
  [/^\/kits\/runs\/[^/]+/, '/kits'],
  // So are a CAPA record and an MES order. Both were missing: after a tenant
  // switch their ids name nothing, and the page sat on a record from the tenant
  // just left.
  [/^\/mes\/orders\/[^/]+/, '/mes'],
  // A cleaning / maintenance task's run belongs to one tenant too (z8nrz7ewt1).
  [/^\/cleaning\/runs\/[^/]+/, '/cleaning?tab=pending-actions'],
  [/^\/equipment\/runs\/[^/]+/, '/equipment?tab=pending-actions'],
  // A storage unit's task run, opened from Inventory's Tasks tab (z8nrz7fjzc).
  [/^\/inventory\/runs\/[^/]+/, '/inventory?tab=tasks'],
  // Back to the builder rather than /form-definitions: reaching the editor
  // requires forms.create/forms.edit, so that destination is always permitted.
  [/^\/form-builder\/[^/]+/, '/form-builder'],
  // A resource profile names one center's member (z8nrz7f0vw); after a switch
  // they may not belong to the new center, so go back to its People list.
  [/^\/resources\/people\/[^/]+/, '/resources'],
  // A Title21 Support ticket belongs to one tenant's site; another tenant
  // cannot see it, so go back to that tenant's Support inbox.
  [/^\/support\/[^/]+/, '/support'],
]

/**
 * The list route to fall back to, or null when the current path is safe to keep
 * (a list, a static page, or anything not tenant-record-specific).
 */
export function fallbackFor(pathname: string): string | null {
  if (NON_RECORD_PATHS.has(pathname)) return null
  for (const [pattern, target] of FALLBACKS) {
    if (pattern.test(pathname)) return target
  }
  return null
}
